import { Plural } from "@lingui/react/macro";
import React, { useCallback, useRef, useState } from "react";
import { worldLocationLabelFromPort } from "src/utils/worldLocationLabel";
import * as styles from "./CruiseItineraryPortItem.module.scss";
import { ResponsiveSanityImage } from "@silversea-ssc/ui-components-react";
import { OpenExcursionModal } from "../../CruiseItinerary";

import { ImageWithAltFragment } from "types/graphql-types";
import {
  ItineraryItemDate,
  ItineraryItemExpand,
  ItineraryItemTitle
} from "../CruiseItineraryItem";
import {
  CategorisedShorex,
  CruiseItineraryItemWithExcursions
} from "../../types";
import { FareCode } from "@silversea-ssc/web-fares";
import clsx from "clsx";
const CruiseItineraryItemDetails = React.lazy(
  () => import("./CruiseItineraryPortItemDetails")
);

interface CruiseItineraryPortItemProps {
  item: CruiseItineraryItemWithExcursions;
  openExcursionModal: OpenExcursionModal;
  hideShorexes: boolean;
  shorexes: CategorisedShorex[];
  shorexesActiveOnFares: FareCode[];
  defaultPortImage?: ImageWithAltFragment;
}

const CruiseItineraryPortItem: React.FC<
  React.PropsWithChildren<CruiseItineraryPortItemProps>
> = ({
  item,
  openExcursionModal,
  hideShorexes,
  shorexes,
  defaultPortImage,
  shorexesActiveOnFares
}) => {
  const [opened, setOpened] = useState(false);

  const port = item.port;
  const worldLocation = port ? worldLocationLabelFromPort(port) : undefined;
  const portType = port?.data?.code?.startsWith("ZZ") ? "day-at-sea" : "port";
  const shorexCount = item.confirmedShorexesCount || 0;
  const portImage = item.portImage || defaultPortImage;

  const detailsRef = useRef<HTMLDivElement | null>(null);
  const toggleDetails = useCallback(
    (
      e:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.KeyboardEvent<HTMLDivElement>
    ) => {
      e.preventDefault();
      if (
        !detailsRef?.current ||
        !detailsRef.current.contains(e.target as Node)
      ) {
        setOpened(!opened);
      }
      //setOpened(!opened);
    },
    [opened]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.code == "Space" || e.code == "Enter") toggleDetails(e);
    },
    [toggleDetails]
  );

  return (
    <div
      className={clsx(styles.item, {
        [styles.opened]: opened
      })}
      onClick={toggleDetails}
      onKeyDown={onKeyDown}
      role="button"
      aria-pressed={opened}
      tabIndex={0}
      style={{ cursor: "pointer" }}
      data-content-piece="details_button"
    >
      <div className={styles.dayNumber}>
        <span>{item.dayNumber?.toString().padStart(2, "0")}</span>
        <ItineraryItemTitle
          className={styles.stopNameInline}
          title={port?.data?.name?.localized}
          portType={portType}
          worldLocation={portType == "port" ? worldLocation : undefined}
        />
      </div>

      <div
        className={clsx(styles.portImageContainer, {
          [styles.collapsed]: !opened
        })}
      >
        {portImage?.asset && (
          <ResponsiveSanityImage
            image={portImage}
            className={styles.portImage}
            alt={port?.data?.name?.localized}
            defaultWidth={175}
            defaultHeight={160}
            loading="lazy"
          />
        )}
      </div>
      <div className={styles.name}>
        <ItineraryItemTitle
          className={styles.stopName}
          title={port?.data?.name?.localized}
          worldLocation={worldLocation}
          portType={portType}
        >
          {!hideShorexes && shorexCount > 0 && (
            <React.Fragment>
              <Plural
                value={shorexCount}
                one="# Excursion"
                other="# Excursions"
              />
            </React.Fragment>
          )}
        </ItineraryItemTitle>
      </div>

      <ItineraryItemDate
        containerClassName={styles.dateTime}
        dateClassName={styles.dateTimeLabel}
        date={item.date}
        arrivalTime={item.arrivalTime}
        departureTime={item.departureTime}
        isOvernight={item.isOvernight}
      />

      <ItineraryItemExpand
        opened={opened}
        className={clsx(styles.expand, {
          [styles.collapsed]: !opened
        })}
      />
      {opened && (
        <div className={styles.details} ref={detailsRef}>
          <React.Suspense>
            <CruiseItineraryItemDetails
              hideShorexes={hideShorexes}
              item={item}
              shorexes={shorexes}
              openExcursionModal={openExcursionModal}
              shorexesActiveOnFares={shorexesActiveOnFares}
            />
          </React.Suspense>
        </div>
      )}
    </div>
  );
};

export default CruiseItineraryPortItem;
